import { makeAutoObservable } from 'mobx'
import { LoadSurgicalOrderDocument } from 'domain/usecases/surgical-order/load-surgical-order-document'
import { UploadSurgicalOrderDocument } from 'domain/usecases/surgical-order/upload-surgical-order-document'
import { LoadSurgicalOrder } from 'domain/usecases/surgical-order/load-surgical-order'
import { LoadCid } from 'domain/usecases/surgical-order/load-cid'
import { ConcludeSurgicalOrder } from 'domain/usecases/surgical-order/conclude-surgical-order'
import { DeleteSurgicalOrderSolicitation } from 'domain/usecases/surgical-order/delete-surgical-order-solicitation'
import { UploadSurgicalOrderDocumentsSameType } from 'domain/usecases/surgical-order/upload-surgical-order-documents-same-type'
import { LoadFavoriteSurgicalOrders } from 'domain/usecases/surgical-order/load-favorite-surgical-orders'
import { DeleteFavoritedSurgicalOrder } from 'domain/usecases/surgical-order/delete-favorited-surgical-order'
import { SurgicalOrderCheckin } from 'domain/usecases/surgical-order/checkin'
import { LoadFavoriteDoctorSurgicalOrders } from 'domain/usecases/surgical-order/load-favorite-doctor-order'
import { UploadToMergeDocument } from 'domain/usecases/surgical-order/upload-to-merge-document'
import { ConfirmMergeDocuments } from 'domain/usecases/surgical-order/confirm-merge-documents'
import { DeleteUploadedDocuments } from 'domain/usecases/surgical-order/delete-uploaded-documents'
import { LoadGuidesByType } from 'domain/usecases/surgical-order/load-guides-by-type'
import { CreateSurgicalOrderStatus } from 'domain/usecases/surgical-order/create-surgical-order-status'
import { LoadReasonsCancellation } from 'domain/usecases/surgical-order-cancellation/load-reasons-to-cancellation'
import { CancelSurgicalOrder } from 'domain/usecases/surgical-order-cancellation/cancel-surgical-order'
import { CheckGuidesGenerated } from 'domain/usecases/surgical-order/check-guides-generated'
import { RegenerateGuides } from 'domain/usecases/surgical-order/regenerate-guides'
import { SearchSurgicalOrdersNew } from 'domain/usecases/surgical-order/search-surgical-orders-new'
import { GetSurgialOrderCounter } from 'domain/usecases/surgical-order/get-surgical-order-counter'
import { FinishActiveSearchers } from 'domain/usecases/surgical-order-cancellation/finish-active-searcher'
import { GetFinalizeSurgicalOrdersHistory } from 'domain/usecases/surgical-order/get-finalize-surgical-orders-history'
import { EditFinalizeSurgicalOrder } from 'domain/usecases/surgical-order/edit-finalize-surgical-order'
import { CreateOrUpdateBudget } from 'domain/usecases/surgical-pendency/create-or-update-budget'
import { SearchSurgicalOrdersSchedules } from 'domain/usecases/surgical-order/search-surgical-orders-schedules'
import { UpdateSurgicalOrderClinicalRecord } from 'domain/usecases/surgical-order/update-surgical-order-clinical-record'
import { GetPresignedUrl } from 'domain/usecases/surgical-order/get-presigned-url'
import { UploadFileToS3 } from 'domain/usecases/surgical-order/upload-file-to-s3'
import { ProcessDocumentToMerge } from 'domain/usecases/surgical-order/process-document-to-merge'
import { GetPresignedUrlCrmo } from 'domain/usecases/surgical-order/get-presigned-url-crmo'
import { ProcessDocumentCrmo } from 'domain/usecases/surgical-order/process-document-crmo'
import { MergeDocumentsCrmo } from 'domain/usecases/surgical-order/merge-documents-crmo'

type Services = {
  loadSurgicalOrder: LoadSurgicalOrder
  loadSurgicalOrderDocument: LoadSurgicalOrderDocument
  uploadSurgicalOrderDocument: UploadSurgicalOrderDocument
  loadCids: LoadCid
  concludeSurgicalOrder: ConcludeSurgicalOrder
  deleteSurgicalOrderSolicitation: DeleteSurgicalOrderSolicitation
  uploadSurgicalOrderDocumentsSameType: UploadSurgicalOrderDocumentsSameType
  loadFavoriteSurgicalOrders: LoadFavoriteSurgicalOrders
  loadFavoriteDoctorSurgicalOrders: LoadFavoriteDoctorSurgicalOrders
  deleteFavoritedSurgicalOrder: DeleteFavoritedSurgicalOrder
  checkin: SurgicalOrderCheckin
  uploadToMergeDocument: UploadToMergeDocument
  confirmMergeDocuments: ConfirmMergeDocuments
  deleteUploadedDocuments: DeleteUploadedDocuments
  loadGuidesByType: LoadGuidesByType
  createSurgicalOrderStatus: CreateSurgicalOrderStatus
  loadReasonsCancellation: LoadReasonsCancellation
  cancelSurgicalOrder: CancelSurgicalOrder
  checkGuidesGenerated: CheckGuidesGenerated
  regenerateGuides: RegenerateGuides
  searchSurgicalOrdersNew: SearchSurgicalOrdersNew
  getSurgicalOrderCounter: GetSurgialOrderCounter
  finishActiveSearchers: FinishActiveSearchers
  getFinalizeSurgicalOrdersHistory: GetFinalizeSurgicalOrdersHistory
  editFinalizeSurgicalOrder: EditFinalizeSurgicalOrder
  createOrUpdateBudget: CreateOrUpdateBudget
  searchSurgicalOrdersSchedules: SearchSurgicalOrdersSchedules
  updateSurgicalOrderClinicalRecord: UpdateSurgicalOrderClinicalRecord
  getPresignedUrl: GetPresignedUrl
  uploadFileToS3: UploadFileToS3
  processDocumentToMerge: ProcessDocumentToMerge
  getPresignedUrlCrmo: GetPresignedUrlCrmo
  processDocumentCrmo: ProcessDocumentCrmo
  mergeDocumentsCrmo: MergeDocumentsCrmo
}

export class SurgicalOrderService {
  constructor(private readonly services: Services) {
    makeAutoObservable(this)
  }

  async loadSurgicalOrderDocument(
    params: LoadSurgicalOrderDocument.Params
  ): Promise<LoadSurgicalOrderDocument.Model> {
    return this.services.loadSurgicalOrderDocument.load(
      params.surgical_order_id,
      params.document_id
    )
  }

  async uploadSurgicalOrderDocument(
    params: UploadSurgicalOrderDocument.Params
  ): Promise<UploadSurgicalOrderDocument.Model> {
    return this.services.uploadSurgicalOrderDocument.upload(params)
  }

  async loadSurgicalOrder(
    id: number,
    params: LoadSurgicalOrder.Params
  ): Promise<LoadSurgicalOrder.Model> {
    return this.services.loadSurgicalOrder.load(id, params)
  }

  async loadCids(params?: LoadCid.Params): Promise<LoadCid.Model> {
    return this.services.loadCids.load(params)
  }

  async concludeSurgicalOrder(
    params: ConcludeSurgicalOrder.Params
  ): Promise<ConcludeSurgicalOrder.Model> {
    return this.services.concludeSurgicalOrder.conclude(params)
  }

  async deleteSurgicalOrderSolicitation(
    params: DeleteSurgicalOrderSolicitation.Params
  ): Promise<DeleteSurgicalOrderSolicitation.Model> {
    return this.services.deleteSurgicalOrderSolicitation.delete(params)
  }

  async uploadSurgicalOrderDocumentsSameType(
    params: UploadSurgicalOrderDocumentsSameType.Params
  ): Promise<UploadSurgicalOrderDocumentsSameType.Model> {
    return this.services.uploadSurgicalOrderDocumentsSameType.upload(params)
  }

  async uploadToMergeDocument(
    params: UploadToMergeDocument.Params
  ): Promise<UploadToMergeDocument.Model> {
    return this.services.uploadToMergeDocument.upload(params)
  }

  async loadFavoriteSurgicalOrders(
    params: LoadFavoriteSurgicalOrders.Params
  ): Promise<LoadFavoriteSurgicalOrders.Model> {
    return this.services.loadFavoriteSurgicalOrders.load(params)
  }

  async loadFavoriteDoctorSurgicalOrders(
    params: LoadFavoriteDoctorSurgicalOrders.Params
  ): Promise<LoadFavoriteDoctorSurgicalOrders.Model> {
    return this.services.loadFavoriteDoctorSurgicalOrders.load(params)
  }

  async deleteFavoritedSurgicalOrder(
    params: DeleteFavoritedSurgicalOrder.Params
  ): Promise<DeleteFavoritedSurgicalOrder.Model> {
    return this.services.deleteFavoritedSurgicalOrder.delete(params)
  }

  async checkin(
    params: SurgicalOrderCheckin.Params
  ): Promise<SurgicalOrderCheckin.Model> {
    return this.services.checkin.checkin(params)
  }

  async confirmMergeDocuments(
    params: ConfirmMergeDocuments.Params
  ): Promise<ConfirmMergeDocuments.Model> {
    return this.services.confirmMergeDocuments.confirm(params)
  }

  async deleteUploadedDocuments(
    params: DeleteUploadedDocuments.Params
  ): Promise<DeleteUploadedDocuments.Model> {
    return this.services.deleteUploadedDocuments.delete(params)
  }

  async loadGuidesByType(
    surgical_order_id: number,
    type: string,
    params: LoadGuidesByType.Params
  ): Promise<LoadGuidesByType.Model> {
    return this.services.loadGuidesByType.load(surgical_order_id, type, params)
  }

  async createSurgicalOrderStatus(
    params: CreateSurgicalOrderStatus.Params
  ): Promise<CreateSurgicalOrderStatus.Model> {
    return this.services.createSurgicalOrderStatus.create(params)
  }

  async loadReasonsCancellation(): Promise<LoadReasonsCancellation.Model> {
    return await this.services.loadReasonsCancellation.load()
  }

  async cancelSurgicalOrder(
    params: CancelSurgicalOrder.Params
  ): Promise<CancelSurgicalOrder.Model> {
    return this.services.cancelSurgicalOrder.cancel(params)
  }

  async checkGuidesGenerated(
    params: CheckGuidesGenerated.Params
  ): Promise<CheckGuidesGenerated.Model> {
    return this.services.checkGuidesGenerated.check(params)
  }

  async regenerateGuides(
    params: RegenerateGuides.Params
  ): Promise<RegenerateGuides.Model> {
    return this.services.regenerateGuides.regenerate(params)
  }

  async searchSurgicalOrders(
    params: SearchSurgicalOrdersNew.Params
  ): Promise<SearchSurgicalOrdersNew.Model> {
    return this.services.searchSurgicalOrdersNew.load(params)
  }

  async getSurgicalOrderCounter(
    params: GetSurgialOrderCounter.Params
  ): Promise<GetSurgialOrderCounter.Model> {
    return this.services.getSurgicalOrderCounter.count(params)
  }

  async finishActiveSearchers(
    prams: FinishActiveSearchers.Params
  ): Promise<FinishActiveSearchers.Model> {
    return this.services.finishActiveSearchers.finish(prams)
  }

  async getFinalizeSurgicalOrdersHistory(
    params: GetFinalizeSurgicalOrdersHistory.Params
  ): Promise<GetFinalizeSurgicalOrdersHistory.Model> {
    return this.services.getFinalizeSurgicalOrdersHistory.load(params)
  }
  async editFinalizeSurgicalOrder(
    params: EditFinalizeSurgicalOrder.Params
  ): Promise<EditFinalizeSurgicalOrder.Model> {
    return this.services.editFinalizeSurgicalOrder.finish(params)
  }

  async createOrUpdateBudget(
    params: CreateOrUpdateBudget.Params
  ): Promise<CreateOrUpdateBudget.Model> {
    return this.services.createOrUpdateBudget.add(params)
  }

  async searchSurgicalOrdersSchedules(
    params: SearchSurgicalOrdersSchedules.Params
  ): Promise<SearchSurgicalOrdersSchedules.Model> {
    return this.services.searchSurgicalOrdersSchedules.load(params)
  }

  async updateSurgicalOrderClinicalRecord(
    surgical_oder_id: number,
    params: UpdateSurgicalOrderClinicalRecord.Params
  ): Promise<UpdateSurgicalOrderClinicalRecord.Model> {
    return this.services.updateSurgicalOrderClinicalRecord.update(
      surgical_oder_id,
      params
    )
  }

  async getPresignedUrl(
    params: GetPresignedUrl.Params
  ): Promise<GetPresignedUrl.Model> {
    return this.services.getPresignedUrl.get(params)
  }

  async uploadFileToS3(params: UploadFileToS3.Params): Promise<void> {
    return this.services.uploadFileToS3.upload(params)
  }

  async processDocumentToMerge(
    params: ProcessDocumentToMerge.Params
  ): Promise<ProcessDocumentToMerge.Model> {
    return this.services.processDocumentToMerge.process(params)
  }

  async getPresignedUrlCrmo(
    params: GetPresignedUrlCrmo.Params
  ): Promise<GetPresignedUrlCrmo.Model> {
    return this.services.getPresignedUrlCrmo.get(params)
  }

  async processDocumentCrmo(
    params: ProcessDocumentCrmo.Params
  ): Promise<ProcessDocumentCrmo.Model> {
    return this.services.processDocumentCrmo.process(params)
  }

  async mergeDocumentsCrmo(
    params: MergeDocumentsCrmo.Params
  ): Promise<MergeDocumentsCrmo.Model> {
    return this.services.mergeDocumentsCrmo.merge(params)
  }
}

export default SurgicalOrderService
